import {
  Briefcase,
  Camera,
  Check,
  Loader2,
  Mail,
  Phone,
  ShieldCheck,
  Trash2,
  User,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { buildAuthHeaders, trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Skeleton } from '../../ui/skeleton';
import { UserAvatar } from '../../ui/user-avatar';
import { useMutationFeedback, useToast } from '../toast';
import { ImageCropperModal } from './ImageCropperModal';

export function ProfileForm() {
  const utils = trpc.useUtils();
  const { data: user, isLoading, isError } = trpc.users.me.useQuery();
  const { toastError } = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [selectedFileForCrop, setSelectedFileForCrop] = useState<File | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateProfileMutation = trpc.users.updateProfile.useMutation(
    useMutationFeedback({
      successMessage: 'Profile saved successfully!',
      invalidateQueries: [utils.users.me],
    })
  );

  /**
   * Avatar upload is a single authenticated PUT of the cropped bytes rather
   * than presign -> PUT -> updateProfile. The Worker validates the image and
   * writes the user row in one operation, so there is no window where the
   * object exists but nothing references it.
   */
  const putAvatar = async (blob: Blob): Promise<string> => {
    const res = await fetch(`${import.meta.env.VITE_WORKER_URL}/avatars/me`, {
      method: 'PUT',
      headers: { ...(await buildAuthHeaders()), 'Content-Type': 'image/png' },
      body: blob,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error || 'Failed to upload profile picture');
    }
    return ((await res.json()) as { avatarUrl: string }).avatarUrl;
  };

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setJobTitle(user.jobTitle || '');
      setPhoneNumber(user.phoneNumber || '');
      setAvatarUrl(user.avatarUrl || null);
    }
  }, [user]);

  const handleAvatarFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toastError('Please select a valid image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toastError('Image size must be less than 10MB');
      return;
    }

    setSelectedFileForCrop(file);
    setIsCropperOpen(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    setIsUploading(true);
    try {
      setAvatarUrl(await putAvatar(croppedBlob));
      // The Worker already persisted avatar_url; refetch so every other consumer
      // of users.me (sidebar, facepiles) picks the new one up.
      await utils.users.me.invalidate();
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Error uploading profile picture';
      toastError(msg);
    } finally {
      setIsUploading(false);
      setSelectedFileForCrop(null);
    }
  };

  const handleRemoveAvatar = async () => {
    const previous = avatarUrl;
    setAvatarUrl(null);
    try {
      const res = await fetch(`${import.meta.env.VITE_WORKER_URL}/avatars/me`, {
        method: 'DELETE',
        headers: await buildAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to remove profile picture');
      await utils.users.me.invalidate();
    } catch (err: unknown) {
      // Put it back rather than showing a cleared avatar that was never cleared.
      setAvatarUrl(previous);
      toastError(err instanceof Error ? err.message : 'Error removing profile picture');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate({
      firstName,
      lastName,
      jobTitle,
      phoneNumber,
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-96 mb-6" />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-20 rounded-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Error Loading Profile</CardTitle>
            <CardDescription>
              Unable to load your profile information. Please try refreshing the page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const isSaving = updateProfileMutation.isPending;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Profile Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage your account profile picture, contact details, and role.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Profile Picture Card */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Profile Picture</CardTitle>
            <CardDescription>
              Your avatar is displayed across workspace comments, tasks, and team member lists.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative group">
              <UserAvatar
                user={{
                  firstName,
                  lastName,
                  fullName: user.fullName,
                  avatarUrl,
                  email: user.email,
                  jobTitle,
                }}
                size="xl"
                showTooltip={false}
                className="h-24 w-24 border-2 border-border shadow-inner"
              />

              {isUploading && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-xs rounded-full flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 text-center sm:text-left">
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarFileSelect}
                  accept="image/png,image/jpeg,image/webp,image/jpg"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploading || isSaving}
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2"
                >
                  <Camera className="h-4 w-4" />
                  {avatarUrl ? 'Change Picture' : 'Upload Picture'}
                </Button>

                {avatarUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isUploading || isSaving}
                    onClick={handleRemoveAvatar}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1.5"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Supports PNG, JPG, or WebP. Max file size 5MB.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Personal Details Card */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Personal & Work Details</CardTitle>
            <CardDescription>
              Keep your profile up to date so team members can contact and identify you.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-xs font-medium">
                  First Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-xs font-medium">
                  Last Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="jobTitle" className="text-xs font-medium">
                  Job Title
                </Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="jobTitle"
                    type="text"
                    placeholder="e.g. Account Executive / Support Lead"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phoneNumber" className="text-xs font-medium">
                  Phone Number
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  value={user.email}
                  disabled
                  className="pl-9 bg-muted/50 cursor-not-allowed pr-28"
                />
                <div className="absolute right-3 top-2 flex items-center">
                  <Badge variant="secondary" className="text-xs gap-1 py-0.5 font-normal">
                    <ShieldCheck className="h-3 w-3 text-emerald-500" />
                    Verified
                  </Badge>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Email address is managed by your account authentication provider.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="submit" disabled={isSaving || isUploading} className="min-w-[120px]">
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </form>

      <ImageCropperModal
        isOpen={isCropperOpen}
        imageFile={selectedFileForCrop}
        onClose={() => {
          setIsCropperOpen(false);
          setSelectedFileForCrop(null);
        }}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
}
